import { Injectable } from '@angular/core';
import { PersonEditorCompanyConfig, PersonEditorEnums, PersonEditorForms } from '../models/person';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { emailValidatorRegex } from '../utils/regex-patterns';
import { formatDatePickerValue } from '../utils/datepicker-value-parser';
import { EnumService } from './enum.service';
import { map } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { Enum } from '../models/base';
import { CompaniesService } from './companies.service';
import { AdminService } from './admin.service';
import { EmailDomainBlacklistValidator } from '../utils/emailDomainBlacklistValidator';

@Injectable({
  providedIn: 'root'
})
// responsible for all of the people editor initialization logic
export class PersonInitService {
  constructor(
    private adminService: AdminService,
    private companyService: CompaniesService,
    private enumService: EnumService,
    private formBuilder: UntypedFormBuilder,
    private emailBlacklistValidator: EmailDomainBlacklistValidator
  ) {}

  public buildForms(): PersonEditorForms {
    let forms: PersonEditorForms = new PersonEditorForms();

    forms.PersonalDetails = this.formBuilder.group({
      EmploymentTypeId: new UntypedFormControl(),
      UseEIN: new UntypedFormControl(),
      FederalIdentifier: new UntypedFormControl(),
      VerifyFederalIdentifier: new UntypedFormControl(),
      IsCompany: new UntypedFormControl(),
      FirstName: new UntypedFormControl('', Validators.required),
      MiddleName: new UntypedFormControl(),
      LastName: new UntypedFormControl('', Validators.required),
      PersonalEmailAddress: new UntypedFormControl(
        '',
        [Validators.email],
        [this.emailBlacklistValidator.validate.bind(this.emailBlacklistValidator)]
      ),
      Birthday: new UntypedFormControl(),
      HomePhoneNumber: new UntypedFormControl(),
      MobilePhoneNumber: new UntypedFormControl(),
      Gender: new UntypedFormControl(),
      MaritalStatus: new UntypedFormControl(),
      Race: new UntypedFormControl(),
      StreetAddress: new UntypedFormControl(),
      StreetAddress2: new UntypedFormControl(),
      State: new UntypedFormControl(),
      City: new UntypedFormControl(),
      PostalCode: new UntypedFormControl(),
      IsNonUSAddress: new UntypedFormControl(),
      Country: new UntypedFormControl()
    });

    forms.JobDetails = this.formBuilder.group({
      EmployeeId: new UntypedFormControl('', [
        Validators.maxLength(4),
        Validators.pattern('[0-9]*$')
      ]),
      HireDate: new UntypedFormControl(formatDatePickerValue(new Date())),
      ShouldReportAsNewHire: new UntypedFormControl(),
      WorksIn: new UntypedFormControl('', Validators.required),
      OKToAutoPay: new UntypedFormControl(),
      PayFrequency: new UntypedFormControl(),
      Location: new UntypedFormControl(),
      Division: new UntypedFormControl(),
      Department: new UntypedFormControl(),
      JobTitle: new UntypedFormControl(),
      IsSeasonal: new UntypedFormControl(),
      FullTimePartTime: new UntypedFormControl('F'),
      IsOwnerOfficer: new UntypedFormControl(),
      SOCCode: new UntypedFormControl(),
      OccupationId: new UntypedFormControl(),
      BasisOfPayment: new UntypedFormControl(),
      WorkEmailAddress: new UntypedFormControl(
        '',
        [Validators.email],
        [this.emailBlacklistValidator.validate.bind(this.emailBlacklistValidator)]
      ),
      WorkPhoneNumber: new UntypedFormControl(),
      OriginalHireDate: new UntypedFormControl(),
      IncludeNonW2InWorkersComp: new UntypedFormControl(),
      WorkersCompCode: new UntypedFormControl(),
      WorkersCompOwnerStatus: new UntypedFormControl(),
      WorkersCompLimitId: new UntypedFormControl(),
      // used only to mark valid/dirty based on labor allocation total
      // in order to prevent user from changing tabs when total != 100
      LaborAllocationTotal: new UntypedFormControl()
    });

    forms.Benefits = this.formBuilder.group({
      PTO: this.formBuilder.array([])
    });

    forms.DirectDeposit = this.formBuilder.group({
      Accounts: this.formBuilder.array([])
    });

    forms.AuditLog = this.formBuilder.group({});

    return forms;
  }

  public getCompanyConfigs(clientCode: string) {
    const config = new PersonEditorCompanyConfig();

    const obs = [
      this.companyService.getDivisions(clientCode).pipe(map(data => (config.Divisions = data))),
      this.companyService.getPTOConfigs(clientCode).pipe(map(data => (config.PTOs = data))),
      // i know, i know.... this is not a company level config object,
      // but this seems to be the best place for it right now
      this.adminService.getWorkersCompLimits().pipe(map(data => (config.WorkersCompLimits = data)))
    ];

    return forkJoin(obs).pipe(map(x => config));
  }

  public getEnums() {
    const enums = new PersonEditorEnums();
    enums.Genders = [new Enum('M', 'Male'), new Enum('F', 'Female')];

    let obs = [
      this.enumService.get('DeductionTypes').pipe(map(data => (enums.BenefitTypes = data))),
      this.enumService.get('EarningType').pipe(map(data => (enums.EarningTypes = data))),
      this.enumService.get('EmpBasisOfPayment').pipe(map(data => (enums.BasisOfPayments = data))),
      this.enumService.get('EmployeeStatuses').pipe(map(data => (enums.EmployeeStatuses = data))),
      this.enumService
        .get('EmployeeStatusReason')
        .pipe(map(data => (enums.EmployeeStatusReasons = data))),
      this.enumService.get('FUTAEXEMPT').pipe(map(data => (enums.FutaReasons = data))),
      this.enumService.get('EmpMaritalStatus').pipe(map(data => (enums.MaritalStatuses = data))),
      this.enumService.get('EmployeeRace').pipe(map(data => (enums.Races = data))),
      this.enumService.get('OCCUPATIONS').pipe(map(data => (enums.Occupations = data))),
      this.enumService.get('OrganizationTypes').pipe(map(data => (enums.OrganizationTypes = data))),
      this.enumService.get('SOC').pipe(map(data => (enums.Soccodes = data)))
    ];

    return forkJoin<any>(obs).pipe(map(x => enums));
  }

  public setTabOrder(forms: PersonEditorForms) {
    return {
      1: forms.PersonalDetails,
      2: forms.JobDetails,
      3: forms.EarningsAndDeductions,
      4: new UntypedFormGroup({}),
      5: forms.Benefits,
      6: forms.DirectDeposit,
      7: forms.AuditLog
    };
  }
}
